import { makeAutoObservable, reaction, runInAction, type IReactionDisposer } from 'mobx'
import { type ReactNode } from 'react'
import { type IDropdownItem } from 'shared/ui'
import { RequestLoadData } from 'shared/api/lib'
import {
  campaignActiveFilter,
  CampaignApi,
  type IParamsGetList,
  type IResponseShortCampaign,
} from 'entities/Campaign'

export class CampaignSelectStore {
  private _disposeParams: IReactionDisposer
  page = 1
  total = 0
  limit = 20
  search = ''
  loading = false
  loadingMore = false

  selectedItemId: null | number = null
  selectedItem: IDropdownItem | null = null

  error: ReactNode = null

  private _dropDownItems: IDropdownItem[] = []

  constructor() {
    makeAutoObservable(this)

    this.loadData()
    this._disposeParams = reaction(
      () => this.params,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  dispose = () => {
    this._disposeParams()
    this._requestLoadData.dispose()
  }

  get dropDownItems(): IDropdownItem[] {
    return this._dropDownItems
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        if (this.page === 1) {
          this.loading = true
          this._dropDownItems = []
        }
      })

      const { data } = await CampaignApi.getList(this.params, {
        cancelToken: cancelRequest.token,
      })

      if (data) {
        runInAction(() => {
          if (this.page === 1) {
            this._dropDownItems = data.data.map(this.responseToDropdownItem)
          } else {
            this._dropDownItems = [
              ...this.dropDownItems,
              ...data.data.map(this.responseToDropdownItem),
            ]
          }

          this.total = data.meta.total
        })
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        this.loading = false
        this.loadingMore = false
      })
    },
  })

  loadData = async () => {
    return this._requestLoadData.loadData()
  }

  get params(): IParamsGetList {
    return {
      page: this.page,
      limit: this.limit,
      search: this.search || '',
      filters: campaignActiveFilter,
    }
  }

  private responseToDropdownItem = (response: IResponseShortCampaign): IDropdownItem => {
    return {
      id: response.id,
      label: response.name,
    }
  }

  setDropdownItem = (id: number, item: IDropdownItem) => {
    this.selectedItemId = id
    this.selectedItem = item
  }

  onChangeOpen = (value: boolean) => {
    if (!value) {
      this.setSearch('')
    }

    if (value && !this._dropDownItems.length) {
      this.loadData()
    }
  }

  setSearch = (value: string) => {
    this.page = 1
    this.total = 1
    this.search = value
  }

  onLoadMore = () => {
    if (this.total <= this.dropDownItems.length) return
    if (this.loadingMore) return
    this.loadingMore = true
    this.page += 1
  }
}
